import { useCallback, useEffect, useRef } from 'react';
import { useApolloClient } from '@apollo/client';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { CloseCode } from 'graphql-ws';

import {
  actionSetScopeStatusIntervals,
  actionSetShiftStatusIntervals,
} from 'lib/actions';
import { GET_EXECUTION_INTERVALS } from 'lib/api/queries';
import { wsLink } from 'lib/api/links/graphql';
import { checkIsLatestShiftActive } from 'lib/selectors/checkIsLatestShiftActive';
import { getIsInitialDataSet } from 'lib/selectors/getIsInitialDataSet';
import { getLatestMasterExecutionIntervals } from 'lib/selectors/getLatestMasterExecutionIntervals';
import { getMachine } from 'lib/selectors/getMachine';
import { getScopeStart } from 'lib/selectors/getScopeStart';
import { getShiftStart } from 'lib/selectors/getShiftStart';
import { getStartOfCurrentShopDay } from 'lib/selectors/getStartOfCurrentShopDay';
import { startOfAppHistory } from 'lib/utils/date';

const RESYNC_RETRIES = 2;
// Doubles per retry with 1-2x jitter, so a retry can still land within the
// few seconds Categorize's submit waits for fresh history.
const RESYNC_RETRY_DELAY_MS = 1000;
const PASSIVE_RESYNC_COOLDOWN_MS = 15000;
// Network and server recoveries hit every tablet at once, and graphql-ws only
// spreads its reconnects over 1-5s.
const RECONNECT_RESYNC_JITTER_MS = 30000;
// A failed resync can restart the socket itself (lib/api/links), so this counts
// attempts rather than successes.
const RECONNECT_RESYNC_MIN_INTERVAL_MS = 60000;
// The downtime popup opens Categorize on its own for every long downtime.
const CATEGORIZE_FRESH_MS = 60000;
// A request that hangs shouldn't hold off other triggers for long.
const RESYNC_IN_FLIGHT_MS = 10000;

const toMs = (time) => {
  return new Date(time).getTime();
};

// The tablet's history reaching the window start may itself be stale, and a
// response starting inside it would merge with it rather than replace it.
// Downtime merges touching idle and unavailable intervals, so step back
// through those too.
const getFetchStart = (windowStart, statusIntervals) => {
  let first = statusIntervals.findIndex((interval) => {
    return (
      toMs(interval.start) < toMs(windowStart) &&
      (!interval.end || toMs(interval.end) >= toMs(windowStart))
    );
  });
  if (first === -1) {
    return windowStart;
  }
  while (
    first > 0 &&
    statusIntervals[first].label !== 'ACTIVE' &&
    statusIntervals[first - 1].label !== 'ACTIVE' &&
    toMs(statusIntervals[first - 1].end) === toMs(statusIntervals[first].start)
  ) {
    first -= 1;
  }
  const historyStart = startOfAppHistory();
  const fetchStart =
    toMs(statusIntervals[first].start) < toMs(historyStart)
      ? historyStart
      : statusIntervals[first].start;
  return toMs(fetchStart) > toMs(windowStart) ? windowStart : fetchStart;
};

// A timestamp ahead of now (the clock was set back) isn't recent.
const isRecent = (timestamp, windowMs) => {
  const elapsed = Date.now() - timestamp;
  return elapsed >= 0 && elapsed < windowMs;
};

// The execution subscription only covers scope start onward, so history the tablet
// missed before it (asleep, offline) would otherwise show as phantom downtime.
const useStatusIntervalsResync = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const client = useApolloClient();
  const machineRef = useSelector(getMachine)?.machineRef;
  const isLatestShiftActive = useSelector(checkIsLatestShiftActive);
  const shiftStart = useSelector(getShiftStart);
  const startOfCurrentShopDay = useSelector(getStartOfCurrentShopDay);
  const scopeStart = useSelector(getScopeStart);
  const windowStart = isLatestShiftActive ? shiftStart : startOfCurrentShopDay;
  const requestCountRef = useRef(0);
  const latestAppliedRequestRef = useRef(0);
  // Requests applied, dropped or out of retries; a pending retry isn't done.
  const finishedRequestRef = useRef(0);
  const lastRequestAtRef = useRef(0);
  const finishRequest = (request) => {
    finishedRequestRef.current = Math.max(finishedRequestRef.current, request);
  };
  const isResyncInFlight = () => {
    return (
      requestCountRef.current > finishedRequestRef.current &&
      isRecent(lastRequestAtRef.current, RESYNC_IN_FLIGHT_MS)
    );
  };
  const retryTimeoutRef = useRef(null);
  const runResyncRef = useRef(null);
  const isMountedRef = useRef(true);
  const lastAppliedAtRef = useRef(0);
  const lastReconnectResyncAtRef = useRef(0);
  const appliedWaitersRef = useRef([]);
  const settleAppliedWaiters = () => {
    appliedWaitersRef.current.splice(0).forEach((resolve) => {
      resolve();
    });
  };

  // Before the initial load, GET_INITIAL_DATA fetches this window anyway.
  const canResync = useCallback(() => {
    return !!(
      machineRef &&
      windowStart &&
      getIsInitialDataSet(store.getState())
    );
  }, [machineRef, store, windowStart]);

  const runResync = useCallback(
    async (attempt) => {
      if (!canResync()) {
        if (attempt > 0) {
          finishRequest(requestCountRef.current);
        }
        return;
      }
      clearTimeout(retryTimeoutRef.current);
      requestCountRef.current += 1;
      const request = requestCountRef.current;
      lastRequestAtRef.current = Date.now();
      const latestPushAtRequest = getLatestMasterExecutionIntervals(
        store.getState()
      );
      const startDate = getFetchStart(
        windowStart,
        store.getState().statusIntervals
      );

      let executionIntervals;
      try {
        const { data } = await client.query({
          query: GET_EXECUTION_INTERVALS,
          variables: { machineRef, startDate },
          fetchPolicy: 'no-cache',
        });
        executionIntervals = data?.executionIntervals;
      } catch {
        if (isMountedRef.current && request === requestCountRef.current) {
          if (attempt < RESYNC_RETRIES) {
            retryTimeoutRef.current = setTimeout(() => {
              runResyncRef.current(attempt + 1);
            }, RESYNC_RETRY_DELAY_MS * 2 ** attempt * (1 + Math.random()));
            return;
          }
          // Nothing more is coming, so don't hold up a waiting submit.
          settleAppliedWaiters();
        }
        finishRequest(request);
        return;
      }

      const state = store.getState();
      if (
        !isMountedRef.current ||
        !executionIntervals?.length ||
        request < latestAppliedRequestRef.current ||
        getMachine(state)?.machineRef !== machineRef
      ) {
        if (request === requestCountRef.current) {
          settleAppliedWaiters();
        }
        finishRequest(request);
        return;
      }
      finishRequest(request);
      latestAppliedRequestRef.current = request;
      lastAppliedAtRef.current = Date.now();
      dispatch(actionSetShiftStatusIntervals(executionIntervals));

      // A subscription push that landed while this was in flight is newer than
      // the response, so lay it back over the scope window it covers.
      const latestPush = getLatestMasterExecutionIntervals(state);
      if (latestPush && latestPush !== latestPushAtRequest) {
        dispatch(actionSetScopeStatusIntervals(latestPush));
      }
      settleAppliedWaiters();
    },
    [canResync, client, dispatch, machineRef, store, windowStart]
  );
  runResyncRef.current = runResync;

  const jitterTimeoutRef = useRef(null);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      clearTimeout(retryTimeoutRef.current);
      clearTimeout(jitterTimeoutRef.current);
      settleAppliedWaiters();
    };
  }, []);

  const resync = useCallback(() => {
    return runResync(0);
  }, [runResync]);

  // Waking or reconnecting can fire several of these together. A failed resync
  // doesn't start the cooldown, so a reconnect can still repair what it missed.
  const hasRecentlyResynced = () => {
    return isRecent(lastAppliedAtRef.current, PASSIVE_RESYNC_COOLDOWN_MS);
  };

  const resyncIfIdle = useCallback(() => {
    if (!hasRecentlyResynced()) {
      resync();
    }
  }, [resync]);

  const scheduleReconnectResync = useCallback((event, delayMs) => {
    clearTimeout(jitterTimeoutRef.current);
    jitterTimeoutRef.current = setTimeout(() => {
      resyncAfterReconnectRef.current(event);
    }, delayMs);
  }, []);

  // Any resync applied since the reconnect already repaired it, including one
  // that was still in flight then (waking fires the tab's reload first), and
  // one started since then that is still in flight will.
  const resyncAfterReconnect = useCallback(
    (event) => {
      if (
        !canResync() ||
        hasRecentlyResynced() ||
        latestAppliedRequestRef.current > event.applied ||
        (requestCountRef.current > event.requests && isResyncInFlight())
      ) {
        return;
      }
      const lastAttemptAt = lastReconnectResyncAtRef.current;
      if (isRecent(lastAttemptAt, RECONNECT_RESYNC_MIN_INTERVAL_MS)) {
        scheduleReconnectResync(
          event,
          lastAttemptAt + RECONNECT_RESYNC_MIN_INTERVAL_MS - Date.now()
        );
        return;
      }
      lastReconnectResyncAtRef.current = Date.now();
      resync();
    },
    [canResync, resync, scheduleReconnectResync]
  );
  const resyncAfterReconnectRef = useRef(resyncAfterReconnect);
  resyncAfterReconnectRef.current = resyncAfterReconnect;

  // Checked here too: the jitter can outlast the cooldown.
  const resyncAfterReconnectSoon = useCallback(() => {
    if (hasRecentlyResynced()) {
      return;
    }
    scheduleReconnectResync(
      {
        applied: latestAppliedRequestRef.current,
        requests: requestCountRef.current,
      },
      Math.random() * RECONNECT_RESYNC_JITTER_MS
    );
  }, [scheduleReconnectResync]);

  const previousScopeStartRef = useRef(scopeStart);
  useEffect(() => {
    const previousScopeStart = previousScopeStartRef.current;
    previousScopeStartRef.current = scopeStart;
    if (previousScopeStart && scopeStart && scopeStart !== previousScopeStart) {
      resync();
    }
  }, [scopeStart, resync]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        resyncIfIdle();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [resyncIfIdle]);

  // graphql-ws silently reconnects after a dropped socket, so the subscription
  // never errors. The planned token-refresh close (4403) isn't an outage.
  const wasDisconnectedRef = useRef(false);
  const requestsAtLastGapRef = useRef(0);
  useEffect(() => {
    const removeOnClosed = wsLink.client.on('closed', (event) => {
      if (event?.code !== CloseCode.Forbidden) {
        wasDisconnectedRef.current = true;
        requestsAtLastGapRef.current = requestCountRef.current;
      }
    });
    const removeOnConnected = wsLink.client.on('connected', () => {
      if (wasDisconnectedRef.current) {
        wasDisconnectedRef.current = false;
        resyncAfterReconnectSoon();
      }
    });
    window.addEventListener('online', resyncAfterReconnectSoon);
    return () => {
      removeOnClosed();
      removeOnConnected();
      window.removeEventListener('online', resyncAfterReconnectSoon);
    };
  }, [resyncAfterReconnectSoon]);

  // The live subscription may have missed pushes since this request count.
  const noteExecutionError = useCallback(() => {
    requestsAtLastGapRef.current = requestCountRef.current;
  }, []);

  // The recovered subscription's first push covers scope start onward, and a
  // reload applied or still in flight since the error covers the rest.
  const resyncAfterRecovery = useCallback(() => {
    const startedSinceGap =
      requestCountRef.current > requestsAtLastGapRef.current;
    if (
      latestAppliedRequestRef.current > requestsAtLastGapRef.current ||
      (startedSinceGap && isResyncInFlight())
    ) {
      return;
    }
    resync();
  }, [resync]);

  // Skips only when a resync started after the last socket drop or execution
  // subscription error applied recently. Otherwise resolves once a resync
  // applies, whichever trigger started it, so a failed first attempt doesn't
  // end submit's wait before its retry lands.
  const resyncForCategorize = useCallback(() => {
    const isFresh =
      latestAppliedRequestRef.current > requestsAtLastGapRef.current &&
      isRecent(lastAppliedAtRef.current, CATEGORIZE_FRESH_MS);
    if (isFresh || !canResync()) {
      return Promise.resolve();
    }
    const applied = new Promise((resolve) => {
      appliedWaitersRef.current.push(resolve);
    });
    resync();
    return applied;
  }, [canResync, resync]);

  return {
    resync,
    resyncForCategorize,
    resyncAfterRecovery,
    noteExecutionError,
  };
};

export { useStatusIntervalsResync };
